import { measurementCreated, measurementListOutput, measurementPath, measurementSummaryOutput, TRAINING_ROUTES, } from "@repo/contract/training";
import { readJson } from "../../api/auth-api";
import { getJson, sendNoContent, withQuery, } from "../core/request";
import { exerciseKeys } from "../exercise/keys";
import { profileKeys, profileWriteInvalidates } from "./keys";
/**
 * `POST /me/measurements` — append one point.
 *
 * ── THE IDEMPOTENCY KEY IS THE CALLER'S, AND IT TRAVELS UNTOUCHED ───────────
 * `input.idempotencyKey` is sent exactly as given and never minted here. The
 * key identifies an ATTEMPT: the caller mints it once and resends the SAME
 * one on every retry of that attempt (the outbox does, T21), which is what
 * turns a lost response into a `200` instead of a twin row. A key minted in
 * this function would be a fresh key per retry — the duplicate it exists to
 * prevent. Omitted, the server keeps the old append-always semantics.
 */
export async function recordMeasurement(api, input) {
    const response = await api.authorizedFetch(TRAINING_ROUTES.measurements, {
        body: JSON.stringify(input),
        headers: { "content-type": "application/json" },
        method: "POST",
    });
    const created = response.status === 201;
    const row = await readJson(response, measurementCreated);
    return { ...row, created };
}
/** `GET /me/measurements?kind&from&to` — one series, oldest first. `latest`
 *  is the whole series' tail whatever range was asked. */
export function fetchMeasurements(api, query, init) {
    return getJson(api, withQuery(TRAINING_ROUTES.measurements, {
        kind: query.kind,
        from: query.from,
        to: query.to,
    }), measurementListOutput, init);
}
/** `DELETE /me/measurements/:id` — soft delete, `204`. A second call is a
 *  `404`, which arrives as the server's `ApiError`. A correction is a new
 *  {@link recordMeasurement} plus this on the wrong row. */
export function deleteMeasurement(api, measurementId) {
    return sendNoContent(api, "DELETE", measurementPath(measurementId));
}
export function measurementsOptions(api, query) {
    return {
        queryFn: ({ signal }) => fetchMeasurements(api, query, { signal }),
        queryKey: profileKeys.measurements(query.kind, query.from, query.to),
    };
}
/**
 * `GET /me/measurement-summary?family=` — every series of one kind family as
 * its newest point and the one before (`D207`): the "Medidas" card's one read
 * instead of one per site.
 */
export function fetchMeasurementSummary(api, query, init) {
    return getJson(api, withQuery(TRAINING_ROUTES.measurementSummary, { family: query.family }), measurementSummaryOutput, init);
}
export function measurementSummaryOptions(api, query) {
    return {
        queryFn: ({ signal }) => fetchMeasurementSummary(api, query, { signal }),
        queryKey: profileKeys.measurementSummary(query.family),
    };
}
/** What a point written or deleted changes: every series (the server
 *  normalises the kind), the family summaries beside them (`D207`), and the
 *  two reads every profile write moves. */
function measurementWriteInvalidates() {
    return [
        ...profileWriteInvalidates(profileKeys.measurementsAll),
        profileKeys.measurementSummaryAll,
        // A weigh-in also places the strength levels (`D208`).
        exerciseKeys.strengthAll,
    ];
}
/** Invalidates every series, not only `input.kind`: the server normalises the
 *  kind, so the spelling a series is cached under is not knowable here. */
export function recordMeasurementMutation(api) {
    return {
        invalidates: measurementWriteInvalidates(),
        mutationFn: (input) => recordMeasurement(api, input),
        mutationKey: ["profile", "measurements", "record"],
    };
}
export function deleteMeasurementMutation(api) {
    return {
        invalidates: measurementWriteInvalidates(),
        mutationFn: (measurementId) => deleteMeasurement(api, measurementId),
        mutationKey: ["profile", "measurements", "delete"],
    };
}
//# sourceMappingURL=measurements.js.map